import { useToast } from "@/components/ui/use-toast";
import { ApiError } from "@/services/api";
import { useTranslation } from "react-i18next";
import { getApiErrorDescription } from "@/lib/utils/getApiErrorDescription";
import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from "@tanstack/react-query";

export function useMutationWithErrorHandling<
  TData,
  TError extends ApiError,
  TVariables,
  TContext,
>(
  options?: UseMutationOptions<TData, TError, TVariables, TContext> & {
    errorTitle?: string;
  },
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    errorTitle,
    onError: onErrorFromProps,
    ...restOptions
  } = options || {};
  const defaultErrorHandler = (error: TError) => {
    toast({
      title: errorTitle || t("errors.genericTitle"),
      description: getApiErrorDescription(error, t),
      variant: "destructive",
    });
  };

  return useMutation<TData, TError, TVariables, TContext>({
    ...restOptions,
    onError: onErrorFromProps || defaultErrorHandler,
  });
}
